import { guid }                            from 'rewire-common';
import { observable, freeze }              from 'rewire-core';
import {
  ICell,
  IColumn,
  IGrid,
  IGridOptions,
  IRowData,
  createGrid,
  createColumn,
  getNumberString,
}                                          from 'rewire-grid';
import {
  Form,
  IFormContext,
}                                          from 'rewire-ui';
import {
  numberColumn,
  ITimecardTest,
  ITimecardTestDetail,
}                                          from '../DataTypes';
import DataQueries                         from '../graphql/Queries';
import {
  StatusIconRenderer,
  DifferenceTotalRenderer,
}                                          from './TestCellRenderers';

interface ITimecardTestExpectedResultsModelState {
  grid:  IGrid;
  test?: ITimecardTest
}

export class TimecardTestExpectedResultsModel {
  state: ITimecardTestExpectedResultsModelState;
  form:  Form;

  constructor() {
    this.state = observable({
      grid: {} as IGrid,
      test: undefined,
    });

    const tagSearcher = {
      async search(search?: string) {
        return await DataQueries.getTags('documents', search);
      },
      map(item?: any) {
        return (item && !Array.isArray(item) && item) || '';
      }
    };

    this.form = Form.create((form: IFormContext) => ({
      name:            form.string().label('Test Description'),
      tags:            form.multiselectautocomplete(tagSearcher, {chipLimit: 3, inputAdd: (v: string) => v}).label('Tags').placeholder('enter Tags'),
      expectedGross:   form.static().label('Expected Gross'),
      calculatedGross: form.static().label('Calculated Gross'),
      difference:      form.static().label('Difference'),
    }),
    {},
    {
      defaultAdornmentsEnabled: false,
      variant:                  'standard',
      updateOnChange:           true,
      useTooltipForErrors:      true,
    });

    this.grid = this.createGrid([]);
  }

  reset() {
    this.form.reset();
    freeze(() => {
      this.test    = undefined;
      this.grid.set([]);
    });
  }

  async load(test?: ITimecardTest) {
    this.reset();
    this.test = test;
    try {
      this.grid = this.createGrid(this.test?.details);
      if (test) {
        const expectedGross   = this.numberToFormattedString(test.total);
        const calculatedGross = this.numberToFormattedString(test.total + test.difference);
        const difference      = this.numberToFormattedString(test.difference);
        this.form.value       = {name: test.name, tags: test.tags, expectedGross: expectedGross, calculatedGross: calculatedGross, difference: difference};
      }
    } catch (err) {
      this.reset();
    }
  }

  get grid(): IGrid {
    return this.state.grid;
  }

  set grid(grid: IGrid) {
    this.state.grid = grid;
  }

  get test(): ITimecardTest | undefined {
    return this.state.test;
  }

  set test(value: ITimecardTest | undefined) {
    this.state.test = value;
  }

  private numberToFormattedString(value: number) {
    return getNumberString(value, numberColumn.options.decimals, numberColumn.options.thousandSeparator, numberColumn.options.fixed);
  }

  private get columns(): any[] {
    return [
      {name: 'status',         label: '',                  type: 'text',       width: '35px', fixed: true, renderer: StatusIconRenderer},
      {name: 'units',          label: 'Units',             type: numberColumn, width: '75px'},
      {name: 'rate',           label: 'Rate',              type: numberColumn, width: '75px'},
      {name: 'factor',         label: 'Factor',            type: numberColumn, width: '75px'},
      {name: 'reason',         label: 'Reason/Premium',    type: 'text',       width: '125px', map: (v: any) => v && v.name},
      {name: 'overtimeReason', label: 'OT Reason',         type: 'text',       width: '125px', map: (v: any) => v && v.name},
      {name: 'amount',         label: 'Expected Gross',    type: numberColumn, width: '115px'},
      {name: 'calcAmount',     label: 'Calculated Gross',  type: numberColumn, width: '115px'},
      {name: 'difference',     label: 'Difference',        type: numberColumn, width: '115px', renderer: DifferenceTotalRenderer},
    ];
  }

  private createGrid(results: ITimecardTestDetail[] | undefined): IGrid {
    const cols:    IColumn[]    = this.createColumns();
    const rows:    IRowData[]   = this.createRows(results, cols);
    const options: IGridOptions = this.createOptions();
    const grid:    IGrid        = createGrid(rows, cols, options);
    grid.rowHeight              = 32;
    return grid;
  }

  private createOptions(): IGridOptions {
    return {
      readOnly:             true,
      clearSelectionOnBlur: true,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D':      (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {}
      }
    };
  }

  private createColumns(): IColumn[] {
    const cols: IColumn[] = [];

    for (const col of this.columns) {
      const { name, label, ...opts } = col;
      opts.canSort = false;
      const column = createColumn(
        name,
        label,
        opts
      );
      if (opts?.options) column.typeOptions = opts.options;
      cols.push(column);
    }

    return cols;
  }

  private createRows(rowData: ITimecardTestDetail[] | undefined, _cols: IColumn[]): IRowData[] {
    const rows: IRowData[] = [];

    if (rowData) {
      rowData.forEach((row) => {
        const r: IRowData = { id: `${guid()}`, data: row };
        r.data!['calcAmount'] = row.amount + row.difference;
        rows.push(r);
      });
    }
    return rows;
  }

  hasChanges(): boolean {
    return this.form.hasChanges;
  }

  cancel() {
    this.form.revert();
  }

  save(): boolean {
    return this.form.submit();
  }
}

const timecardTestExpectedResultsModel = new TimecardTestExpectedResultsModel();
export default timecardTestExpectedResultsModel;